import React, { useEffect, useState } from "react";
import { Dialog, IconButton, CircularProgress, makeStyles } from "@material-ui/core";
import { GetApp, Close, OpenInNew } from "@material-ui/icons";
import api from "../../services/api";

// Cartão de arquivo no padrão do WhatsApp Web: selo com a extensão, nome e botão de baixar.
// PDF abre num visualizador na própria tela; os demais tipos baixam.

const EXT_COLORS = {
  pdf: "#e5484d",
  doc: "#2b7de9", docx: "#2b7de9", odt: "#2b7de9", rtf: "#2b7de9", txt: "#667781",
  xls: "#1f9d55", xlsx: "#1f9d55", xlsm: "#1f9d55", csv: "#1f9d55", ods: "#1f9d55",
  ppt: "#e8710a", pptx: "#e8710a", odp: "#e8710a",
  zip: "#8d6e63", rar: "#8d6e63", "7z": "#8d6e63", tar: "#8d6e63", gz: "#8d6e63",
  apk: "#3ddc84", exe: "#546e7a",
};

// "1759672800123_contrato_final.pdf" -> { name: "contrato_final.pdf", ext: "pdf" }
export const fileInfoFromUrl = (url, fallbackName) => {
  let base = "";
  try {
    base = decodeURIComponent(String(url || "").split("?")[0].split("#")[0].split("/").pop() || "");
  } catch (e) {
    base = String(url || "").split("/").pop() || "";
  }
  base = base.replace(/^\d{10,}[_-]/, "");
  const name = base || fallbackName || "arquivo";
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 && name.length - dot <= 11 ? name.slice(dot + 1).toLowerCase() : "";
  return { name, ext };
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    card: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 240,
      maxWidth: 340,
      margin: "0 0 4px -3px",
      padding: "9px 8px 9px 10px",
      borderRadius: 6,
      backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(11,20,26,0.05)",
      cursor: "pointer",
      userSelect: "none",
      "&:hover": {
        backgroundColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(11,20,26,0.08)",
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 1,
      },
    },
    badge: {
      flex: "none",
      width: 34,
      height: 40,
      borderRadius: 5,
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      paddingBottom: 5,
      color: "#ffffff",
      fontSize: 9.5,
      fontWeight: 800,
      letterSpacing: 0.3,
      textTransform: "uppercase",
      position: "relative",
      // canto dobrado
      clipPath: "polygon(0 0, 68% 0, 100% 24%, 100% 100%, 0 100%)",
    },
    text: {
      flex: 1,
      minWidth: 0,
    },
    name: {
      fontSize: 13.5,
      lineHeight: "18px",
      fontWeight: 500,
      color: isDark ? "#e9edef" : "#111b21",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      wordBreak: "break-word",
      whiteSpace: "normal",
    },
    sub: {
      marginTop: 1,
      fontSize: 11.5,
      lineHeight: "15px",
      color: isDark ? "rgba(233,237,239,0.6)" : "#667781",
      whiteSpace: "normal",
    },
    action: {
      flex: "none",
      width: 34,
      height: 34,
      padding: 0,
      borderRadius: "50%",
      border: `1px solid ${isDark ? "rgba(233,237,239,0.28)" : "rgba(11,20,26,0.2)"}`,
      color: isDark ? "rgba(233,237,239,0.7)" : "#54656f",
      "& svg": { fontSize: 19 },
    },
    viewerPaper: {
      width: "min(1100px, 96vw)",
      maxWidth: "none",
      height: "92vh",
      margin: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      borderRadius: 10,
    },
    viewerBar: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      gap: 4,
      padding: "6px 8px 6px 16px",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    viewerTitle: {
      flex: 1,
      minWidth: 0,
      fontSize: 14,
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    viewerBody: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: isDark ? "#111b21" : "#525659",
      color: "#ffffff",
      fontSize: 13.5,
      textAlign: "center",
      padding: 0,
    },
    frame: {
      width: "100%",
      height: "100%",
      border: 0,
      backgroundColor: "#ffffff",
    },
  };
});

const PdfViewer = ({ url, name, onClose, onDownload }) => {
  const classes = useStyles();
  const [blobUrl, setBlobUrl] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let created = "";
    // Baixa como blob: funciona mesmo com o backend em outro domínio e sem depender de o
    // servidor permitir exibir o arquivo dentro de um quadro.
    api
      .get(url, { responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        created = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        setBlobUrl(created);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (created) window.URL.revokeObjectURL(created);
    };
  }, [url]);

  return (
    <Dialog open onClose={onClose} classes={{ paper: classes.viewerPaper }} aria-label={name}>
      <div className={classes.viewerBar}>
        <span className={classes.viewerTitle}>{name}</span>
        <IconButton size="small" onClick={() => window.open(url, "_blank", "noopener,noreferrer")} aria-label="Abrir em nova aba" title="Abrir em nova aba">
          <OpenInNew />
        </IconButton>
        <IconButton size="small" onClick={onDownload} aria-label="Baixar" title="Baixar">
          <GetApp />
        </IconButton>
        <IconButton size="small" onClick={onClose} aria-label="Fechar" title="Fechar">
          <Close />
        </IconButton>
      </div>
      <div className={classes.viewerBody}>
        {failed ? (
          <span style={{ padding: 24 }}>Não foi possível abrir o arquivo aqui. Use "Baixar" para salvar uma cópia.</span>
        ) : blobUrl ? (
          <iframe className={classes.frame} src={blobUrl} title={name} />
        ) : (
          <CircularProgress style={{ color: "#ffffff" }} />
        )}
      </div>
    </Dialog>
  );
};

const FileCard = ({ url, fallbackName, onDownload }) => {
  const classes = useStyles();
  const [viewerOpen, setViewerOpen] = useState(false);
  const { name, ext } = fileInfoFromUrl(url, fallbackName);
  const isPdf = ext === "pdf";

  const download = (e) => {
    if (e) e.stopPropagation();
    onDownload(url, name);
  };
  const open = () => (isPdf ? setViewerOpen(true) : download());

  return (
    <>
      <div
        className={classes.card}
        role="button"
        tabIndex={0}
        onClick={open}
        onDoubleClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        title={isPdf ? "Abrir" : "Baixar"}
      >
        <span className={classes.badge} style={{ backgroundColor: EXT_COLORS[ext] || "#8696a0" }}>
          {(ext || "arq").slice(0, 4)}
        </span>
        <span className={classes.text}>
          <div className={classes.name}>{name}</div>
          <div className={classes.sub}>
            {ext ? ext.toUpperCase() : "Arquivo"} · {isPdf ? "clique para abrir" : "clique para baixar"}
          </div>
        </span>
        <IconButton className={classes.action} onClick={download} aria-label={`Baixar ${name}`}>
          <GetApp />
        </IconButton>
      </div>
      {viewerOpen && (
        <PdfViewer url={url} name={name} onClose={() => setViewerOpen(false)} onDownload={download} />
      )}
    </>
  );
};

export default FileCard;
